import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Play, Pause, RotateCcw } from 'lucide-react';

const APPLIANCE_COLORS = {
  airfryer: { ring: '#f97316', bg: 'bg-orange-50', done: 'bg-orange-100' },
  vapeur:   { ring: '#3b82f6', bg: 'bg-blue-50',   done: 'bg-blue-100' },
  four:     { ring: '#ef4444', bg: 'bg-red-50',     done: 'bg-red-100' },
  poele:    { ring: '#eab308', bg: 'bg-yellow-50',  done: 'bg-yellow-100' },
  classic:  { ring: '#6b7280', bg: 'bg-gray-50',    done: 'bg-gray-100' },
};

const TimerWidget = ({ minutes, label, appliance = 'airfryer', compact = false }) => {
  const totalSeconds = minutes * 60;
  const [remaining, setRemaining] = useState(totalSeconds);
  const [running, setRunning] = useState(false);
  const [done, setDone] = useState(false);
  const intervalRef = useRef(null);

  const reset = useCallback(() => {
    clearInterval(intervalRef.current);
    setRemaining(totalSeconds);
    setRunning(false);
    setDone(false);
  }, [totalSeconds]);

  useEffect(() => {
    if (running) {
      intervalRef.current = setInterval(() => {
        setRemaining(r => {
          if (r <= 1) {
            clearInterval(intervalRef.current);
            setRunning(false);
            setDone(true);
            return 0;
          }
          return r - 1;
        });
      }, 1000);
    } else {
      clearInterval(intervalRef.current);
    }
    return () => clearInterval(intervalRef.current);
  }, [running]);

  useEffect(() => { reset(); }, [minutes, reset]);

  const colors = APPLIANCE_COLORS[appliance] || APPLIANCE_COLORS.airfryer;
  const progress = 1 - remaining / totalSeconds;
  const mm = String(Math.floor(remaining / 60)).padStart(2, '0');
  const ss = String(remaining % 60).padStart(2, '0');

  const radius = compact ? 24 : 36;
  const size = compact ? 60 : 88;
  const circumference = 2 * Math.PI * radius;
  const dashOffset = circumference * (1 - progress);

  if (compact) {
    return (
      <div className={`flex items-center gap-2 px-3 py-2 rounded-xl ${done ? colors.done : colors.bg}`}>
        <div className="relative" style={{ width: size, height: size }}>
          <svg className="w-full h-full -rotate-90" viewBox={`0 0 ${size} ${size}`}>
            <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="#e5e7eb" strokeWidth="4" />
            <circle
              cx={size / 2} cy={size / 2} r={radius} fill="none"
              stroke={done ? '#22c55e' : colors.ring}
              strokeWidth="4" strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={dashOffset}
              className="transition-all duration-1000"
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-xs font-bold tabular-nums">
              {done ? '✓' : `${mm}:${ss}`}
            </span>
          </div>
        </div>
        <div className="flex flex-col gap-1">
          {label && <span className="text-xs font-medium text-gray-600">{label}</span>}
          <div className="flex gap-1">
            <button
              onClick={() => setRunning(r => !r)}
              disabled={done}
              className="p-1.5 rounded-lg bg-white border border-gray-200 text-gray-600 hover:text-gray-800 disabled:opacity-40 transition-colors"
            >
              {running ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3" />}
            </button>
            <button onClick={reset} className="p-1.5 rounded-lg bg-white border border-gray-200 text-gray-500 hover:text-gray-700 transition-colors">
              <RotateCcw className="w-3 h-3" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`flex flex-col items-center gap-3 p-4 rounded-2xl transition-colors ${done ? 'bg-green-50 border-2 border-green-400' : colors.bg}`}>
      {label && <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</p>}
      <div className="relative" style={{ width: size, height: size }}>
        <svg className="w-full h-full -rotate-90" viewBox={`0 0 ${size} ${size}`}>
          <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="#f3f4f6" strokeWidth="6" />
          <circle
            cx={size / 2} cy={size / 2} r={radius} fill="none"
            stroke={done ? '#22c55e' : colors.ring}
            strokeWidth="6" strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={dashOffset}
            className="transition-all duration-1000"
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className={`text-xl font-bold tabular-nums ${done ? 'text-green-600' : 'text-gray-800'}`}>
            {done ? '✓' : `${mm}:${ss}`}
          </span>
        </div>
      </div>
      {done && <p className="text-green-600 font-semibold text-sm animate-pulse">Cuisson terminée !</p>}
      <div className="flex gap-2">
        <button
          onClick={() => setRunning(r => !r)}
          disabled={done}
          className="flex items-center gap-1.5 bg-orange-500 hover:bg-orange-600 disabled:bg-gray-300 text-white px-4 py-2 rounded-xl text-sm font-medium transition-colors"
        >
          {running ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
          {running ? 'Pause' : (remaining === totalSeconds ? 'Démarrer' : 'Reprendre')}
        </button>
        <button onClick={reset} className="p-2 rounded-xl border border-gray-200 text-gray-500 hover:text-gray-700 hover:bg-white transition-colors">
          <RotateCcw className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default TimerWidget;
